import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Container, Form, Button, Card, Alert, Spinner } from "react-bootstrap";
import {
  FaUser,
  FaLock,
  FaCashRegister,
  FaStore,
  FaShieldAlt,
} from "react-icons/fa";

const API_URL = process.env.REACT_APP_API_URL;

function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [adminPin, setAdminPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isAdminLogin, setIsAdminLogin] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // First, authenticate with username and password
      const response = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Login failed");
      }

      // If this is an admin login attempt, verify the admin PIN
      if (isAdminLogin) {
        // Verify admin PIN
        if (adminPin !== "vm2002@DB!") {
          throw new Error("Invalid admin PIN");
        }

        // Check if user has admin role
        if (data.user.role !== "admin") {
          throw new Error(
            "Admin access required. This account does not have admin privileges.",
          );
        }

        // Store admin session
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
        localStorage.setItem("adminAuthenticated", "true");
        localStorage.setItem("adminLoginTime", new Date().toISOString());

        // Redirect to admin dashboard
        navigate("/admin/dashboard");
      } else {
        // Regular user login
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));

        if (data.user.expires_in) {
          localStorage.setItem("expires_in", data.user.expires_in);
        }
        if (data.user.last_updated) {
          localStorage.setItem("last_updated", data.user.last_updated);
        }

        // Redirect to receipts page
        navigate("/receipts");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleAdminMode = () => {
    setIsAdminLogin(!isAdminLogin);
    setAdminPin("");
    setError("");
  };

  return (
    <Container
      fluid
      className="d-flex justify-content-center align-items-center p-0"
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative Background Elements */}
      <div
        style={{
          position: "absolute",
          top: "-10%",
          right: "-5%",
          width: "300px",
          height: "300px",
          borderRadius: "50%",
          background: "rgba(255,255,255,0.1)",
          zIndex: 1,
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-10%",
          left: "-5%",
          width: "250px",
          height: "250px",
          borderRadius: "50%",
          background: "rgba(255,255,255,0.1)",
          zIndex: 1,
        }}
      />

      {/* Main Content */}
      <div
        style={{
          position: "relative",
          zIndex: 2,
          width: "100%",
          maxWidth: "450px",
        }}
      >
        {/* Logo/Brand Section */}
        <div className="text-center mb-4">
          <div
            className="d-inline-flex justify-content-center align-items-center mb-3"
            style={{
              width: "80px",
              height: "80px",
              background: "white",
              borderRadius: "50%",
              boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
            }}
          >
            <FaCashRegister size={40} color="#667eea" />
          </div>
          <h1
            style={{
              color: "white",
              fontWeight: "bold",
              fontSize: "2.5rem",
              marginBottom: "0.5rem",
            }}
          >
            ISLPOS
          </h1>
          <p style={{ color: "rgba(255,255,255,0.8)", fontSize: "1rem" }}>
            Intelligent Store & Inventory Point of Sale
          </p>
        </div>

        {/* Login Card */}
        <Card
          className="border-0 shadow-lg"
          style={{
            borderRadius: "15px",
            backdropFilter: "blur(10px)",
            background: "rgba(255,255,255,0.95)",
          }}
        >
          <Card.Body className="p-5">
            <h3
              className="text-center mb-4"
              style={{ color: "#333", fontWeight: "600" }}
            >
              {isAdminLogin ? "Admin Access" : "Welcome Back"}
            </h3>
            <p className="text-center text-muted mb-4">
              {isAdminLogin
                ? "Enter admin credentials to access management panel"
                : "Sign in to access your POS dashboard"}
            </p>

            {error && (
              <Alert
                variant="danger"
                className="border-0"
                style={{ borderRadius: "10px" }}
              >
                {error}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-4">
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    disabled={loading}
                    placeholder="Username or Email"
                    style={{
                      paddingLeft: "40px",
                      height: "50px",
                      borderRadius: "10px",
                      border: "1px solid #e0e0e0",
                      fontSize: "1rem",
                    }}
                  />
                  <FaUser
                    style={{
                      position: "absolute",
                      left: "15px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#667eea",
                      fontSize: "1.2rem",
                    }}
                  />
                </div>
              </Form.Group>

              <Form.Group className="mb-4">
                <div className="position-relative">
                  <Form.Control
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    placeholder="Password"
                    style={{
                      paddingLeft: "40px",
                      height: "50px",
                      borderRadius: "10px",
                      border: "1px solid #e0e0e0",
                      fontSize: "1rem",
                    }}
                  />
                  <FaLock
                    style={{
                      position: "absolute",
                      left: "15px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#667eea",
                      fontSize: "1.2rem",
                    }}
                  />
                </div>
              </Form.Group>

              {/* Admin PIN Field - Only show when admin mode is active */}
              {isAdminLogin && (
                <Form.Group className="mb-4">
                  <div className="position-relative">
                    <Form.Control
                      type="password"
                      value={adminPin}
                      onChange={(e) => setAdminPin(e.target.value)}
                      required
                      disabled={loading}
                      placeholder="Admin PIN"
                      style={{
                        paddingLeft: "40px",
                        height: "50px",
                        borderRadius: "10px",
                        border: "1px solid #e0e0e0",
                        fontSize: "1rem",
                      }}
                    />
                    <FaShieldAlt
                      style={{
                        position: "absolute",
                        left: "15px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#764ba2",
                        fontSize: "1.2rem",
                      }}
                    />
                  </div>
                  <Form.Text className="text-muted">
                    Enter your admin security PIN for elevated access
                  </Form.Text>
                </Form.Group>
              )}

              <div className="d-flex justify-content-between align-items-center mb-4">
                <a
                  href="#"
                  style={{
                    color: "#667eea",
                    textDecoration: "none",
                    fontSize: "0.9rem",
                  }}
                  onClick={(e) => e.preventDefault()}
                >
                  Forgot Password?
                </a>
              </div>

              <div className="text-center mt-3">
                <small className="text-muted">
                  Need an account?{" "}
                  <Link
                    to="/signup"
                    style={{ color: "#667eea", textDecoration: "none" }}
                  >
                    Register here
                  </Link>
                </small>
              </div>

              <Button
                type="submit"
                variant="primary"
                className="w-100 border-0"
                disabled={loading}
                style={{
                  height: "50px",
                  borderRadius: "10px",
                  fontSize: "1rem",
                  fontWeight: "600",
                  background: isAdminLogin
                    ? "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                    : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                  transition: "transform 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.transform = "translateY(-2px)")
                }
                onMouseLeave={(e) =>
                  (e.target.style.transform = "translateY(0)")
                }
              >
                {loading ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    {isAdminLogin ? "Verifying Admin..." : "Signing in..."}
                  </>
                ) : isAdminLogin ? (
                  "Access Admin Panel"
                ) : (
                  "Sign In"
                )}
              </Button>
            </Form>

            {/* Footer */}
            <div className="text-center mt-4">
              <small className="text-muted">
                &copy; {new Date().getFullYear()} ISLPOS. All rights reserved.
              </small>
            </div>
          </Card.Body>
        </Card>
        <Button
          variant="link"
          onClick={toggleAdminMode}
          style={{
            color: "#667eea",
            textDecoration: "none",
            fontSize: "0.9rem",
            padding: 0,
          }}
        >
          {isAdminLogin ? "← Back to User Login" : "Admin Login →"}
        </Button>
      </div>
    </Container>
  );
}

export default Login;
